import React from "react";
import { Check, ChevronDown, UserRound } from "lucide-react";
import type { SpeakerCandidate } from "../Dashboard/api";
import { displaySpeaker } from "./transcriptDocument";
import type { UnidentifiedSpeaker } from "./findUnidentifiedSpeakers";

export interface SpeakerToConfirm {
  label: string;
  lines: number;
}

export interface UnidentifiedSpeakersProps {
  speakers: (UnidentifiedSpeaker & { suggestion?: SpeakerCandidate | null })[];
  /** Names not yet confirmed: given at the invite's lower bar, by
   * elimination, or by the model from the conversation (#14225). */
  toConfirm?: SpeakerToConfirm[];
  /** Mark the transcript as correct: every name to confirm at once (#14225). */
  onConfirmAll: () => void;
  /** Go to a speaker's lines and name them there (#12489); the chosen
   * speaker's own card lets them go. */
  onIdentify: (label: string) => void;
  /** The speaker whose lines the reader is going through, if any. */
  chosen?: string | null;
  /** The picker a chosen speaker's controls open, for `aria-controls`. */
  pickerId?: string;
  /** Name a speaker directly: a suggestion taken, or a name confirmed. */
  onAssign: (label: string, name: string, userId?: string) => void;
  /** A name from this list is being saved: nothing else is taken meanwhile. */
  pending?: boolean;
  /** Why the last name taken from this list was not saved. */
  error?: string | null;
}

const lineCount = (lines: number) =>
  `${lines} ${lines === 1 ? "line" : "lines"}`;

/**
 * The transcript's speakers still to settle (#11884): each unnamed voice with
 * what they said at most length and, when its voice sounds like someone, that
 * person to accept in one click; and each name not yet confirmed, as a chip
 * that opens the picker to change it, with one quiet control that marks the
 * transcript as correct (#14225). Naming or confirming a voice teaches later
 * recordings it.
 */
const UnidentifiedSpeakers: React.FC<UnidentifiedSpeakersProps> = ({
  speakers,
  toConfirm = [],
  onConfirmAll,
  onIdentify,
  onAssign,
  pending = false,
  error = null,
  chosen = null,
  pickerId,
}) => {
  if (speakers.length === 0 && toConfirm.length === 0) return null;
  const anySuggestion = speakers.some((s) => s.suggestion);
  return (
    <div className="flex-shrink-0 border-b border-gray-200 bg-white px-4 py-3 md:px-6">
      {speakers.length > 0 && (
        <section aria-label="Unidentified speakers">
          <p className="mb-2 font-mono text-[11px] text-[#6E7480]">
            {speakers.length} {speakers.length === 1 ? "speaker" : "speakers"}{" "}
            not identified
          </p>
          <ul className="flex gap-2 overflow-x-auto pb-1 max-md:flex-col max-md:overflow-x-visible">
            {speakers.map((speaker) => (
              <li
                key={speaker.label}
                className="flex min-w-0 flex-col gap-1 md:w-64 md:shrink-0"
              >
                <button
                  type="button"
                  aria-label={`Identify ${speaker.label}`}
                  aria-expanded={chosen === speaker.label}
                  aria-controls={
                    chosen === speaker.label ? pickerId : undefined
                  }
                  onClick={() => onIdentify(speaker.label)}
                  className={`flex w-full flex-1 cursor-pointer items-start gap-2.5 rounded-[10px] border p-2.5 text-left ${
                    chosen === speaker.label
                      ? "border-[#2A3DCB]/40 bg-[#F7F8FF]"
                      : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[8px] bg-[#ECEDEF] text-[#6E7480]">
                    <UserRound className="h-4 w-4" />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="flex items-baseline gap-1.5">
                      <span className="truncate text-sm text-[#10141B]">
                        {displaySpeaker(speaker.label)}
                      </span>
                      <span className="flex-shrink-0 font-mono text-[11px] text-[#6E7480]">
                        {lineCount(speaker.lines)}
                      </span>
                    </span>
                    {speaker.sample && (
                      <span className="line-clamp-2 text-xs text-[#6E7480]">
                        “{speaker.sample}”
                      </span>
                    )}
                  </span>
                  <ChevronDown
                    aria-hidden="true"
                    className="mt-1.5 h-4 w-4 flex-shrink-0 text-[#6E7480]"
                  />
                </button>
                {speaker.suggestion && (
                  <button
                    type="button"
                    aria-label={`Name ${speaker.label} ${speaker.suggestion.name}`}
                    disabled={pending}
                    onClick={() =>
                      onAssign(
                        speaker.label,
                        speaker.suggestion!.name,
                        speaker.suggestion!.user_id ?? undefined
                      )
                    }
                    className="flex min-w-0 cursor-pointer items-center gap-1.5 self-start rounded-full border border-gray-200 px-2.5 py-1 text-xs text-[#10141B] hover:border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Check
                      aria-hidden="true"
                      className="h-3.5 w-3.5 text-[#6E7480]"
                    />
                    <span className="font-mono text-[11px] text-[#6E7480]">
                      sounds like
                    </span>
                    <span className="truncate">{speaker.suggestion.name}</span>
                  </button>
                )}
                {!speaker.suggestion && anySuggestion && (
                  // Holds the suggestion's row so this card keeps the
                  // height of a neighbour that has one.
                  <span
                    aria-hidden="true"
                    className="invisible rounded-full border px-2.5 py-1 text-xs max-md:hidden"
                  >
                    &nbsp;
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      {toConfirm.length > 0 && (
        <section
          aria-label="Unconfirmed names"
          className={speakers.length > 0 ? "mt-3" : undefined}
        >
          <p className="mb-2 font-mono text-[11px] text-[#6E7480]">
            Unconfirmed names
          </p>
          <ul className="flex flex-wrap items-center gap-1.5">
            {toConfirm.map((speaker) => (
              <li key={speaker.label} className="min-w-0 max-w-full">
                <button
                  type="button"
                  aria-label={`Change ${speaker.label}`}
                  aria-expanded={chosen === speaker.label}
                  aria-controls={
                    chosen === speaker.label ? pickerId : undefined
                  }
                  onClick={() => onIdentify(speaker.label)}
                  className="flex min-w-0 max-w-full cursor-pointer items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-[#10141B] hover:bg-gray-50"
                >
                  <span className="truncate">{speaker.label}</span>
                  <span className="flex-shrink-0 font-mono text-[11px] text-[#6E7480]">
                    {lineCount(speaker.lines)}
                  </span>
                  <ChevronDown
                    aria-hidden="true"
                    className="h-3 w-3 flex-shrink-0 text-[#6E7480]"
                  />
                </button>
              </li>
            ))}
            <li>
              <button
                type="button"
                disabled={pending}
                onClick={onConfirmAll}
                className="cursor-pointer px-1 py-1 text-xs text-[#6E7480] underline-offset-2 hover:text-[#10141B] hover:underline disabled:opacity-50"
              >
                Mark transcript as correct
              </button>
            </li>
          </ul>
        </section>
      )}
      {error && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

export default UnidentifiedSpeakers;
